<!--
  File name: RecordTypeTable.vue
  Program Description:
    - This component renders the list for Record Types
    - This includes the modal containing an input field for adding/updating
      a type of record

  Includes:
    - CrudTable: used to fetch the list of record types and renders it to
      a list table
    - Functions for Create, Read, Update and Delete of a specific type of record
-->
<template>
  <v-container flex>
    <CrudTable
      ref="crudTable"
      v-bind="{
        meta
      }"
      @editRow="editRow"
      @modalChange="modalChange"
    />
    <v-dialog
      id="recordtype-modal"
      v-model="modal"
      width="30%"
      style="z-index:-1000"
      persistent
    >
      <v-card class="pa-4">
        <v-card-title
          class="pb-8"
          color="#162B54"
        >
          <b>{{ modalHeader }}</b>
          <v-spacer />
          <v-icon
            v-if="action === 'view' && readOnly"
            id="close-view-button"
            @click="modal = false"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text>
          <v-text-field
            id="recordType"
            v-model="formItems.value"
            class="required-field"
            label="Record Type"
            :dense="true"
            :flat="readOnly"
            :readonly="readOnly"
            :disabled="readOnly"
            :outlined="!readOnly"
            :error-messages="genericError('formItems.value', 'Record Type')"
            @input="$v.formItems.value.$touch()"
            @blur="$v.formItems.value.$touch()"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <template v-if="action === 'create'">
            <v-btn
              id="cancel-create-button"
              color="primary"
              text
              @click="modal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              id="add-button"
              class="pl-8 pr-8"
              color="primary"
              @click="validate('create')"
            >
              Add
            </v-btn>
          </template>
          <template v-else-if="action === 'view' && readOnly">
            <v-btn
              v-if="showLabel('canDeleteRecordType')"
              :id="`delete-btn-default`"
              :key="`delete-btn-default`"
              class="pl-1 pa-3"
              color="#960500"
              style="font-size: 11px; background-color: #e8b4b0"
              text
              small
              type="button"
              :loading="loading"
              @click="checkDelete()"
            >
              <v-icon class="ma-1">
                {{ 'mdi-delete' }}
              </v-icon> {{ getLabel(`canDeleteRecordType`, 'Delete') }}
            </v-btn>
            <v-btn
              v-if="showLabel('canPatchRecordType')"
              :id="`update-btn-default`"
              :key="`update-btn-default`"
              class="pl-1 pa-3"
              color="#a8a500"
              style="font-size: 11px; background-color: #ebe48f"
              text
              small
              type="button"
              :loading="loading"
              @click="readOnly = false"
            >
              <v-icon class="mr-1">
                {{ 'mdi-pencil' }}
              </v-icon> {{ getLabel(`canPatchRecordType`, 'Edit') }}
            </v-btn>
          </template>
          <template v-else-if="action === 'view' && !readOnly">
            <v-btn
              color="primary"
              text
              @click="readOnly = true, view()"
            >
              Cancel
            </v-btn>
            <v-btn
              class="pl-8 pr-8"
              color="primary"
              @click="validate('update')"
            >
              Update
            </v-btn>
          </template>
          <template v-else-if="action === 'edit'">
            <v-btn
              color="primary"
              text
              @click="modal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              class="pl-8 pr-8"
              color="primary"
              @click="validate('update')"
            >
              Update
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import crud from '@/utils/rest/crud'
import validationErrors from '@/utils/validations/errors'
import { validationMixin } from 'vuelidate'
import table from '@/meta/mdTable/crudModules/recordType/table'
import { VTextField } from 'vuetify/lib'
import { privilegeMixin } from 'session-plugin'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'RecordTypeTable',
  components: {
    VTextField
  },
  mixins: [
    privilegeMixin,
    validationMixin
  ],
  data: () => ({
    modal: false,
    readOnly: false,
    loading: false,
    action: 'create',
    formItems: {
      _id: '',
      value: ''
    }
  }),
  computed: {
    ...validationErrors,
    meta () {
      return table
    },
    schema () {
      return {
        value: this.formItems.value
      }
    },
    modalHeader () {
      if (this.action === 'create') {
        return 'Add Record Type'
      } else {
        return 'Record Type Details'
      }
    }
  },
  methods: {
    // populate main table and subtable
    async populateTable () {
      await this.$refs.crudTable.populateTable()
    },
    // setup edit and shows edit dialog
    async editRow (action, _id) {
      this.formItems._id = _id
      this.action = action
      this.readOnly = false
      await this.view()
      this.modal = !this.modal
    },
    // setup create and view dialog
    async modalChange (action, _id = '') {
      if (action === 'create') {
        this.formItems.value = ''
        this.$v.$reset()
      }
      this.formItems._id = _id
      this.action = action
      action === 'create' ? (this.readOnly = false) : (this.readOnly = true)
      action === 'view' && await this.view()
      this.modal = !this.modal
    },
    // fetch data from database for selected item
    async view () {
      const data = await this.api('read').read(this.formItems._id, '', true)
      this.formItems = { ...this.formItems, ...data.entry }
    },
    // input 'recordType' to crud (imported)
    api (action) {
      return crud('recordType')
    },
    // trigger validation
    // if valid, show confirmation dialog for create or update action
    validate (operation) {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        if (operation === 'create') {
          this.$store.dispatch('showDialog', {
            title: 'Add Record Type',
            message: 'Are you sure you want to add this Record Type?',
            buttonLabel: 'Add',
            buttonColor: 'primary',
            callback: () => this.save(operation)
          })
        } else {
          this.$store.dispatch('showDialog', {
            title: 'Update Record Type',
            message: 'Are you sure you want to update this Record Type?',
            buttonLabel: 'Update',
            buttonColor: 'primary',
            callback: () => this.save(operation)
          })
        }
      }
    },
    // trigger saveItem with specific action prop
    async save (operation) {
      if (operation === 'create') {
        await this.saveItem('create')
      } else {
        await this.saveItem('update')
      }
      this.loading = false
    },
    // trigger api for either create or update depending on action prop
    async saveItem (action) {
      this.loading = true
      const payloadResp = action === 'create' ? await this.api('create').create(this.schema) : await this.api('update').update(this.formItems._id, this.schema)
      const payloadJson = await payloadResp.json()
      this.modal = false
      this.triggerAlert({ success: payloadResp.ok, action, data: payloadJson })
    },
    // show alert dialog for success message or error message
    async triggerAlert ({ success, action, data }) {
      if (success) {
        const createMessage = `${this.formItems.value} has been successfully added!`
        const updateMessage = `${this.formItems.value} has been successfully updated!`
        this.$store.dispatch('showAlert', {
          message: action === 'create' ? createMessage : updateMessage,
          type: 'success'
        })
        this.readOnly = true
        await this.populateTable()
      } else {
        this.$store.dispatch('showAlert', {
          message: data.error || data.message,
          type: 'error'
        })
      }
      this.loading = false
    },
    // show confirmation dialog for delete
    checkDelete (callback) {
      this.$store.dispatch('showDialog', {
        title: 'Delete Record Type',
        message: 'Are you sure that you want to delete this Record Type? This action cannot be undone.',
        buttonLabel: 'Delete',
        callback: () => { this.deleteItem(callback) }
      })
    },
    // delete selected item
    // show alert with either a success message or error message
    async deleteItem (callback) {
      this.modal = false
      this.loading = true
      const resp = await this.api('delete').delete(this.formItems._id)
      this.loading = false
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'Record Type successfully deleted',
          type: 'success'
        })
        await this.populateTable()
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
    }
  },
  validations () {
    return {
      formItems: {
        value: { required }
      }
    }
  }
}
</script>
